import React, { useEffect, useState, useRef, useCallback } from "react";
import { authorization } from "module/auth";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { makeStyles, Popover } from "@material-ui/core";
import cn from "classnames";
import { DownloadIcon, EnterFullScreenIcon } from "@radix-ui/react-icons";
import { useParams } from "react-router-dom";
import Tooltip from "@material-ui/core/Tooltip";
import {
  useTools,
  useEditor,
  TLShapeId,
  TldrawUiIcon,
  exportAs,
  getSnapshot,
  useValue,
  AssetRecordType,
} from "@tldraw/tldraw";
import { createShapeId } from "@tldraw/editor";
import QRCodeLib from "qrcode";
import { useStore } from "store";
import { StoreType } from "store/types";
import CursorToolIcon from "components/icons/components/CursorToolIcon";
import MessageToolIcon from "components/icons/components/MessageToolIcon";
import RectToolIcon from "components/icons/components/RectToolIcon";
import TextToolIcon from "components/icons/components/TextToolIcon";
import ScaleToolIcon from "components/icons/components/ScaleToolIcon";
import HandToolIcon from "components/icons/components/HandToolIcon";
import ArrowToolIcon from "components/icons/components/ArrowToolIcon";
import CircleToolIcon from "components/icons/components/CircleToolIcon";
import PolygonToolIcon from "components/icons/components/PolygonToolIcon";
import LineToolIcon from "components/icons/components/LineToolIcon";
import ShapeToolIcon from "components/icons/components/ShapeToolIcon";
import { KeyboardIcon } from "components/icons/components/KeyboardIcon";
import { useFlyerLayout } from "../../components/SideBar/Aside/components/Settings/useFlyerLayout";
import { useIsBuilderEmptyState } from "pages/flyer/builder/hooks/useIsBuilderEmptyState";
import { getEffectiveFormat } from "../../formats/formatsStore";
import useTooltipStyles from "../components/style";
import { getOrientationStorageKey } from "../../utils/orientationStorage";
import { runTidyUp } from "../utils/tidyUp";
import { setScaleModeCursorActive } from "./scaleResizeCursors";
import {
  safeBringForward,
  safeSendBackward,
  safeSendToBack,
} from "../../components/SideBar/Aside/components/Shapes/UiOverrides";
import { isTypingInInput } from "../utils";
import ImageToolIcon from "components/icons/components/ImageToolIcon";
import IconsToolIcon from "components/icons/components/IconsToolIcon";
import QRcodeIcon from "components/icons/components/QRcodeIcon";
import { RedoIcon, UndoIcon } from "pages/flyer/builder/drawer/components/Primitives/icons";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import { useCommentsStore } from "store/comments";

const SearchExploreIcon = () => (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.56299 0.875C4.08024 0.875 1.25684 3.6984 1.25684 7.18115C1.25684 10.6639 4.08024 13.4873 7.56299 13.4873C9.05427 13.4873 10.4243 12.9766 11.5175 12.1186L14.5112 15.1123C14.7553 15.3564 15.151 15.3564 15.3951 15.1123C15.6392 14.8682 15.6392 14.4725 15.3951 14.2284L12.4014 11.2347C13.2594 10.1415 13.7701 8.77147 13.7701 7.28019C13.7701 3.74549 10.9977 0.875 7.56299 0.875ZM2.50684 7.18115C2.50684 4.38876 4.77059 2.125 7.56299 2.125C10.3063 2.125 12.5701 4.33584 12.5701 7.28019C12.5701 10.0236 10.3554 12.2873 7.56299 12.2873C4.77059 12.2873 2.50684 10.0235 2.50684 7.18115Z"
      fill="#0C172C"
    />
  </svg>
);

const BottomToolPanel = () => {
  const { format, campaignId, clientId, roomId: designId } = useParams();
  const {
    campaign: { flyerType: campaignFlyerType },
    updateCampaign,
    currentOrientations,
    showImagePanel,
    setShowImagePanel,
    showIconsPanel,
    setShowIconsPanel,
    showQRcodePanel,
    setShowQRcodePanel,
    brandKitAssetsPanel,
    setBrandKitAssetsPanel,
  }: StoreType = useStore();
  const isEmptyState = useIsBuilderEmptyState();
  const flyerType = campaignFlyerType || format || "TEARAWAY";

  const classes = useStyles();
  const tools = useTools();
  const editor = useEditor();
  const toolsArray = Object.values(tools);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentTools, setCurrentTools] = useState<any>([]);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [selectedShape, setSelectedShape] = React.useState<any>(null);
  const [copyProps, setCopyProps] = React.useState<any>(null);
  const [selectedToolIds, setSelectedToolIds] = useState<{ [key: string]: string }>({
    select: "select",
    customRect: "customRect",
    customText: "customText",
    message: "message",
  });
  const [selectedIcons, setSelectedIcons] = useState<{ [key: string]: React.ReactElement | null }>({
    select: <CursorToolIcon />,
    customRect: <ShapeToolIcon />,
    customText: <TextToolIcon />,
    message: <MessageToolIcon />,
  });
  const [isScaleMode, setIsScaleMode] = useState(false);
  const isScaleModeRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isScaleModeRef.current = isScaleMode;
  }, [isScaleMode]);

  const [hoveredIconId, setHoveredIconId] = useState<string | null>(null);
  const zoom = useValue("zoom", () => editor.getZoomLevel(), [editor]);
  const [zoomLevel, setZoomLevel] = useState(zoom);
  const selectedShapeIds = useValue("selectedShapeIds", () => editor.getSelectedShapeIds(), [editor]);
  const classesTooltip = useTooltipStyles();

  const iconCn = "w-5 h-5";

  // Helper function to check if current tool is a shape tool
  const isShapeToolActive = () => {
    const currentToolId = editor.getCurrentToolId();
    const shapeTools = ["customRect", "customEllipse", "line", "arrow", "customTriangle"];
    return currentToolId ? shapeTools.includes(currentToolId) : false;
  };

  // Handlers for undo/redo
  const handleUndo = useCallback(() => {
    editor.undo();
  }, [editor]);

  const handleRedo = useCallback(() => {
    editor.redo();
  }, [editor]);

  // Handlers for zoom
  const handleZoomIn = useCallback(() => {
    editor.zoomIn(editor.getViewportScreenCenter(), {
      animation: { duration: 0 },
    });
    setZoomLevel(editor.getZoomLevel());
  }, [editor]);

  const handleZoomOut = useCallback(() => {
    editor.zoomOut(editor.getViewportScreenCenter(), {
      animation: { duration: 0 },
    });
    setZoomLevel(editor.getZoomLevel());
  }, [editor]);

  const handleZoomReset = useCallback(() => {
    editor.resetZoom(editor.getViewportScreenCenter(), {
      animation: { duration: editor.options.animationMediumMs },
    });
    setZoomLevel(editor.getZoomLevel());
  }, [editor]);

  const handleBackToContent = () => {
    const bounds = editor.getSelectionPageBounds() ?? editor.getCurrentPageBounds();

    if (bounds) {
      editor.zoomToBounds(bounds, {
        targetZoom: Math.min(1, editor.getZoomLevel()),
        animation: { duration: 300 },
      });
    }
  };

  useEffect(() => {
    if (format && flyerType !== format) {
      updateCampaign({ flyerType: format || flyerType });
    }
  }, [format, flyerType, updateCampaign]);

  // This hook is controlling the layout of the flyer(orientation, format, etc)
  const [isUpdateShapesPosition, setIsUpdateShapesPosition] = useState(false);
  useFlyerLayout(
    editor,
    flyerType,
    format || flyerType,
    getEffectiveFormat(flyerType) as any,
    isUpdateShapesPosition,
    designId,
    campaignId,
    currentOrientations
  );

  const handleOpenPopover = (
    event: React.MouseEvent<SVGSVGElement | HTMLDivElement>,
    tools: { name: string; shortcut: string }[],
    categoryId: string // Change parameter name to categoryId
  ) => {
    setAnchorEl(event.currentTarget as unknown as HTMLElement);
    setCurrentTools(tools);
    setSelectedTool(categoryId); // Set selectedTool to categoryId
    // Clear hover state when opening popover to prevent incorrect highlighting
    setHoveredIconId(null);
  };

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null);
    setCurrentTools([]);
    setSelectedTool(null);
    // Clear hover state when popover closes to prevent incorrect highlighting
    setHoveredIconId(null);
  }, []);

  // Listen for tour highlight requests — highlight a tool icon during tour steps
  useEffect(() => {
    const handleHighlight = (e: Event) => {
      const toolId = (e as CustomEvent).detail?.toolId || null;
      setHoveredIconId(toolId);
    };
    window.addEventListener("tour:highlight-tool", handleHighlight);
    return () => window.removeEventListener("tour:highlight-tool", handleHighlight);
  }, []);

  // Listen for external close popover requests (e.g., from the tour)
  useEffect(() => {
    const handler = () => handleClosePopover();
    window.addEventListener("tour:close-popover", handler);
    return () => window.removeEventListener("tour:close-popover", handler);
  }, [handleClosePopover]);

  const handleToolClick = (toolId: string, createText?: any, categoryId?: string) => {
    // Picking any other island-toolbar tool exits the comments placing mode
    // and closes the comments right sidebar so the new tool's panel can show.
    const cs = useCommentsStore.getState();
    if (cs.isPlacing) cs.exitPlacingMode();
    if (cs.isHistoryOpen) cs.closeHistory();

    // Notify tour that user selected a tool to create something (hide overlay for drawing)
    const drawingTools = [
      "customRect",
      "customEllipse",
      "customDraw",
      "customHighlight",
      "customText",
      "customArrow",
      "customLine",
    ];
    if (drawingTools.includes(toolId)) {
      window.dispatchEvent(new CustomEvent("tour:creating-start"));
    }

    // Handle Explore Images - open Image search panel in right sidebar
    if (toolId === "exploreImages") {
      editor.setSelectedShapes([]);
      setShowImagePanel(true);
      setShowIconsPanel(false);
      setShowQRcodePanel(false);
      setBrandKitAssetsPanel(null);
      window.dispatchEvent(new CustomEvent("tour:creating-start"));
      return;
    }

    // Handle Brand Images - open Brand Kit images gallery in right sidebar
    if (toolId === "brandImages") {
      editor.setSelectedShapes([]);
      setBrandKitAssetsPanel("image");
      setShowImagePanel(false);
      setShowIconsPanel(false);
      setShowQRcodePanel(false);
      window.dispatchEvent(new CustomEvent("tour:creating-start"));
      return;
    }

    // Handle Icons tool - open Icons panel in right sidebar
    if (toolId === "customIcons") {
      editor.setSelectedShapes([]);
      setShowIconsPanel(true);
      setShowImagePanel(false);
      setShowQRcodePanel(false);
      setBrandKitAssetsPanel(null);
      window.dispatchEvent(new CustomEvent("tour:creating-start"));
      return;
    }

    // Handle QRCode tool - auto-create QR code and show edit panel
    if (toolId === "customQRCode") {
      setShowImagePanel(false);
      setShowIconsPanel(false);
      setBrandKitAssetsPanel(null);
      window.dispatchEvent(new CustomEvent("tour:creating-start"));

      // Check if a QR code already exists
      const allShapes = editor.getCurrentPageShapes();
      const existingQr = allShapes.find(
        (shape: any) =>
          shape.id?.includes?.("qrCode") ||
          (shape.type === "customImage" && (shape.props as any)?.qrCodeLink) ||
          (shape.type === "customImage" && (shape.props as any)?.assetId?.toString()?.includes?.("qr"))
      );

      if (existingQr) {
        // QR code exists — select it and show its edit panel
        editor.setCurrentTool("select");
        editor.select(existingQr.id);
        setShowQRcodePanel(false); // Close the QR create panel, the style panel will show
      } else {
        // No QR code exists — create one with default placeholder
        QRCodeLib.toString("oppizi.com", {
          type: "svg",
          margin: 0,
          width: 300,
          color: { dark: "#000000", light: "#0000" },
        }).then((svgString: string) => {
          const uniqueShapeId = createShapeId();
          const uniqueAssetId = AssetRecordType.createId(
            `qrCode-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          );

          editor.createAssets([
            {
              id: uniqueAssetId,
              type: "image",
              typeName: "asset",
              meta: {},
              props: {
                name: "image",
                src: `data:image/svg+xml;base64,${btoa(svgString)}`,
                w: 150,
                h: 150,
                mimeType: "image/svg+xml",
                isAnimated: false,
              },
            },
          ]);

          // Position QR code at center of viewport
          const viewport = editor.getViewportScreenBounds();
          const center = editor.screenToPage({ x: viewport.width / 2, y: viewport.height / 2 });

          editor.createShape({
            id: uniqueShapeId as any,
            type: "customImage",
            x: center.x - 75,
            y: center.y - 75,
            props: {
              w: 150,
              h: 150,
              assetId: uniqueAssetId,
              qrCodeLink: "",
            },
          });

          // Select the new QR code — this shows the style panel for editing
          editor.setCurrentTool("select");
          editor.select(uniqueShapeId);
          setShowQRcodePanel(false);

          // Trigger shake animation on QR link input to draw attention
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("qr-link-shake"));
          }, 300);
        });
      }
      return;
    }

    // Close all panels if any other tool is selected
    setShowImagePanel(false);
    setShowIconsPanel(false);
    setShowQRcodePanel(false);
    setBrandKitAssetsPanel(null);

    // Clear hover state immediately when a tool is clicked
    setHoveredIconId(null);

    setSelectedTool(toolId);
    if (categoryId) {
      setSelectedToolIds((prev) => ({ ...prev, [categoryId]: toolId }));
      const selectedTool = toolsArray.find((t) => t.id === toolId);
      if (selectedTool) {
        setSelectedIcons((prev) => {
          const newIcon = selectedTool.icon;
          return {
            ...prev,
            // Don't change customRect icon - always keep ShapeToolIcon
            [categoryId]:
              categoryId === "customRect"
                ? prev[categoryId] // Keep existing ShapeToolIcon
                : React.isValidElement(newIcon as unknown as React.ReactElement<any>)
                ? React.cloneElement(newIcon as unknown as React.ReactElement<any>, {
                    isActive: true,
                    isMainPanel: true,
                    color: "white",
                    className: "w-7 h-7",
                  })
                : prev[categoryId],
            select: toolId === "select" ? <CursorToolIcon /> : prev.select,
            // customRect always shows ShapeToolIcon, never changes
            customText: toolId === "customText" ? <TextToolIcon /> : prev.customText,
          };
        });
      }
    }
    if (createText) {
      createText();
    } else {
      const selectedTool = toolsArray.find((t) => t.id === toolId);
      // @ts-ignore
      selectedTool && selectedTool.onSelect();
    }

    if (toolId === "scale") {
      // Activate the select tool first to deactivate Hand
      const selectTool = toolsArray.find((t) => t.id === "select");
      if (selectTool) {
        // @ts-ignore
        selectTool.onSelect();
      }

      setIsScaleMode(true);
      // Activate the CSS-var override for resize-direction cursors so
      // hovering a handle shows the custom scale-arrow glyph. The
      // same toggle runs on the scale-mode-off path below.
      setScaleModeCursorActive(true);

      // Update selectedToolIds to show "scale" as active
      setSelectedToolIds((prev) => ({ ...prev, select: "scale" }));

      // Update icon to show scale icon
      setSelectedIcons((prev) => ({
        ...prev,
        select: <ScaleToolIcon />,
      }));

      const selectedShapeIds = editor.getSelectedShapeIds();
      // List of shape types that don't support custom properties like isScale
      const unsupportedTypes = ["customImage", "line", "arrow", "customLabel", "frame"];
      selectedShapeIds.forEach((id) => {
        const shape = editor.getShape(id);
        // Skip shapes that don't support custom properties
        if (shape && unsupportedTypes.includes(shape.type)) return;
        if (shape) {
          editor.updateShape({
            ...shape,
            props: {
              ...shape.props,
              isScale: true,
            },
          });
        }
      });

      const canvas = document.querySelector(".tl-canvas") as HTMLElement;
      if (canvas) {
        // Scale mode now uses the same cursor as select — no custom
        // cursor override. Clearing the property lets tldraw's
        // default select cursor flow through.
        canvas.style.removeProperty("cursor");
      }
    } else if (toolId === "hand" || toolId === "select") {
      // Deactivate Scale when another tool from the Select group is selected
      setIsScaleMode(false);
      setScaleModeCursorActive(false);
      const selectedShapeIds = editor.getSelectedShapeIds();
      // List of shape types that don't support custom properties like isScale
      const unsupportedTypes = ["customImage", "line", "arrow", "customLabel", "frame"];
      selectedShapeIds.forEach((id) => {
        const shape = editor.getShape(id);
        // Skip shapes that don't support custom properties
        if (shape && unsupportedTypes.includes(shape.type)) return;
        if (shape && (shape.props as any)?.isScale) {
          editor.updateShape({
            ...shape,
            props: {
              ...shape.props,
              isScale: false,
            } as any,
          });
        }
      });
    }

    if (toolId === "hand") {
      const canvas = document.querySelector(".tl-canvas") as HTMLElement;
      if (canvas) {
        canvas.style.setProperty("cursor", "grab");
      }
    }

    handleClosePopover();
  };

  const [icons, setIcons] = useState([
    {
      id: "select",
      tip: "Select (V)",
      component: CursorToolIcon,
      tools: [
        { name: "Select", tip: "Select (V)", shortcut: "V", id: "select", icon: <CursorToolIcon color={"white"} /> },
        { name: "Move", tip: "Move (H)", shortcut: "H", id: "hand", icon: <HandToolIcon color={"white"} /> },
        { name: "Scale", tip: "Scale (K)", shortcut: "K", id: "scale", icon: <ScaleToolIcon color={"white"} /> },
      ],
    },
    {
      id: "customRect",
      tip: "Rectangle (Shift + R)",
      component: ShapeToolIcon,
      tools: [
        {
          name: "Rectangle",
          tip: "Rectangle (Shift + R)",
          shortcut: "Shift + R",
          id: "customRect",
          icon: <RectToolIcon color={"white"} />,
        },
        {
          name: "Line",
          tip: "Line (Shift + L)",
          shortcut: "Shift + L",
          id: "line",
          icon: <LineToolIcon color={"white"} />,
        },
        {
          name: "Circle",
          tip: "Circle (Shift + O)",
          shortcut: "Shift + O",
          id: "customEllipse",
          icon: <CircleToolIcon color={"white"} />,
        },
        {
          name: "Arrow",
          tip: "Arrow (Shift + A)",
          shortcut: "Shift + A",
          id: "arrow",
          icon: <ArrowToolIcon color={"white"} />,
        },
        // {
        //   name: "Star",
        //   tip: "Star",
        //   shortcut: "",
        //   id: "customStar",
        //   icon: <TldrawUiIcon className={iconCn} icon={"geo-star"} />,
        // },
        {
          name: "Polygon",
          tip: "Polygon",
          shortcut: "",
          id: "customTriangle",
          icon: <PolygonToolIcon color={"white"} />,
        },
      ],
    },
    {
      id: "customImage",
      tip: "Image",
      component: ImageToolIcon,
      tools: [
        {
          name: "Explore",
          tip: "Explore Images",
          shortcut: "",
          id: "exploreImages",
          icon: <SearchExploreIcon />,
        },
        {
          name: "Brand Images",
          tip: "Brand Images",
          shortcut: "",
          id: "brandImages",
          icon: <ImageToolIcon />,
        },
      ],
    },
    {
      id: "customText",
      tip: "Text (Shift + T)",
      component: TextToolIcon,
      tools: [],
    },
    {
      id: "customQRCode",
      tip: "QR Code",
      component: QRcodeIcon,
      tools: [],
    },
    {
      id: "customIcons",
      tip: "Icons",
      component: IconsToolIcon,
      tools: [],
    },
    // { id: "message", tip: "Comments", component: MessageToolIcon, tools: [] },
  ]);

  // Monitor tool changes and handle cursor cleanup
  React.useEffect(() => {
    const currentToolId = editor.getCurrentToolId();
    const canvas = document.querySelector(".tl-canvas") as HTMLElement;

    // Close Image/Icons/QRCode panels when tool changes to any drawing/editing tool
    // This handles cases where tools are changed via keyboard shortcuts or other means
    // Keep panel open only for navigation tools (select, hand, scale) as they don't interfere
    if (showImagePanel || showIconsPanel || showQRcodePanel || brandKitAssetsPanel) {
      // Close panel when drawing/editing tools are activated (text, shapes, etc.)
      const drawingTools = [
        "customText",
        "customRect",
        "customEllipse",
        "line",
        "arrow",
        "customTriangle",
        "customDraw",
        "customHighlight",
        "erase",
      ];
      if (currentToolId && drawingTools.includes(currentToolId)) {
        setShowImagePanel(false);
        setShowIconsPanel(false);
        setShowQRcodePanel(false);
        setBrandKitAssetsPanel(null);
      }
    }

    if (canvas) {
      if (currentToolId === "hand") {
        canvas.style.setProperty("cursor", "grab");
      } else if (currentToolId === "select") {
        // Scale mode and plain select both clear the cursor — scale
        // mode now uses the same default cursor as select (was
        // previously overridden with `scaleCursor`).
        canvas.style.removeProperty("cursor");
      } else {
        // Check if it's a shape/image/text tool and set custom crosshair cursor
        const placementTools = [
          "customRect",
          "customEllipse",
          "line",
          "arrow",
          "customTriangle",
          "customText",
          "customImage",
        ];
        if (placementTools.includes(currentToolId || "")) {
          const crosshairCursor = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8 1 H12 V8 H19 V12 H12 V19 H8 V12 H1 V8 H8 Z' fill='white'/%3E%3Cpath d='M9 2 H11 V9 H18 V11 H11 V18 H9 V11 H2 V9 H9 Z' fill='black'/%3E%3C/svg%3E") 10 10, crosshair`;
          canvas.style.setProperty("cursor", crosshairCursor, "important");
        } else if (!["scale", "customDraw", "customHighlight", "erase"].includes(currentToolId || "")) {
          // For other tools, clear cursor unless they need custom cursors
          canvas.style.removeProperty("cursor");
        }
      }
    }
  }, [editor, editor.getCurrentToolId(), isScaleMode]);

  // Sync BottomToolPanel state when tool changes automatically (e.g., after creating a shape)
  // This ensures the visual state matches the actual active tool
  const currentToolId = useValue("currentToolId", () => editor.getCurrentToolId(), [editor]);

  // Track previous tool to detect when we transition from a shape tool to select
  const prevToolIdRef = React.useRef<string | null>(null);
  // Track if we just created a shape to prevent applying isScale to new shapes
  const justCreatedShapeRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (!currentToolId) return;

    // List of shape tools that should reset to ShapeToolIcon when switching to select
    const shapeTools = ["customRect", "customEllipse", "customTriangle", "line", "arrow"];
    // Include text tool in the list
    const toolsThatAutoSwitchToSelect = [...shapeTools, "customText"];

    // Check if we're transitioning from a shape/text tool to select
    const wasShapeOrTextTool = prevToolIdRef.current && toolsThatAutoSwitchToSelect.includes(prevToolIdRef.current);
    const isNowSelect = currentToolId === "select";

    // Update selectedToolIds when tool changes automatically
    // This handles the case when creating a shape automatically switches to "select" tool
    if (currentToolId === "select" || currentToolId === "hand" || currentToolId === "scale") {
      // Determine the actual tool to display based on currentToolId and isScaleMode
      // When scale mode is active, we show "scale" even though the underlying tool is "select"
      let displayToolId = currentToolId;
      if (currentToolId === "select" && isScaleMode) {
        displayToolId = "scale";
      }

      // ONLY when CREATING a shape (transitioning from shape/text tool to select)
      // Deactivate scale mode and switch to select tool
      if (wasShapeOrTextTool && isNowSelect) {
        displayToolId = "select";
        // Mark that we just created a shape to prevent applying isScale to new shapes
        justCreatedShapeRef.current = true;
        // Deactivate scale mode when transitioning from shape/text tools to select (after creating a shape)
        if (isScaleMode) {
          setIsScaleMode(false);
          setScaleModeCursorActive(false);
          // Also remove isScale from selected shapes when creating a new shape
          const selectedShapeIds = editor.getSelectedShapeIds();
          const unsupportedTypes = ["customImage", "line", "arrow", "customLabel", "frame"];
          selectedShapeIds.forEach((id) => {
            const shape = editor.getShape(id);
            if (shape && unsupportedTypes.includes(shape.type)) return;
            if (shape && (shape.props as any)?.isScale) {
              editor.updateShape({
                ...shape,
                props: {
                  ...shape.props,
                  isScale: false,
                } as any,
              });
            }
          });
        }
        // Reset the flag after a short delay to allow other effects to check it
        setTimeout(() => {
          justCreatedShapeRef.current = false;
        }, 100);
      }
      // If we're in select mode but NOT creating a shape, keep scale mode active if it was active
      // This allows selecting shapes while scale tool is active without switching to select tool
      else if (currentToolId === "select" && isScaleMode && !wasShapeOrTextTool) {
        // Keep scale mode active - don't change displayToolId to "select"
        displayToolId = "scale";
        // Reset flag if we're not creating a shape
        justCreatedShapeRef.current = false;
      } else {
        // Reset flag if we're not creating a shape
        justCreatedShapeRef.current = false;
      }

      setSelectedToolIds((prev) => {
        // Only update if the state is different to avoid unnecessary re-renders
        if (prev.select === displayToolId) {
          return prev;
        }
        const updated = { ...prev, select: displayToolId } as any;
        // Note: customRect always stays as "customRect" in selectedToolIds, no need to reset
        return updated;
      });

      // Update the corresponding icon based on displayToolId
      setSelectedIcons((prevIcons) => {
        const newIcon =
          displayToolId === "hand" ? (
            <HandToolIcon />
          ) : displayToolId === "select" ? (
            <CursorToolIcon />
          ) : displayToolId === "scale" ? (
            <ScaleToolIcon />
          ) : (
            prevIcons.select
          );

        // If transitioning from a shape/text tool to select, reset icons appropriately
        const updatedIcons: { [key: string]: React.ReactElement | null } = {
          ...prevIcons,
          select: newIcon,
        };

        if (wasShapeOrTextTool && isNowSelect) {
          // Reset customText icon when transitioning from text tool
          if (prevToolIdRef.current === "customText") {
            updatedIcons.customText = <TextToolIcon />;
          }
          // Note: customRect always shows ShapeToolIcon, no need to reset
        }

        // Only update if something changed
        // But always update if transitioning from shape/text tool to select (to reset icons)
        if (wasShapeOrTextTool && isNowSelect) {
          // Always update when transitioning from shape/text tool to ensure icons are reset
          return updatedIcons;
        }

        if (
          prevIcons.select === newIcon &&
          (prevIcons as any).customRect === updatedIcons.customRect &&
          (prevIcons as any).customText === updatedIcons.customText
        ) {
          return prevIcons;
        }

        return updatedIcons;
      });
    }

    // Update previous tool reference
    prevToolIdRef.current = currentToolId;
  }, [currentToolId, editor, isScaleMode]);

  // Apply isScale to shapes when Scale mode is active and shapes are selected
  // But NOT when we just created a shape (which would deactivate scale mode)
  React.useEffect(() => {
    if (!isScaleMode) return;

    const currentToolId = editor.getCurrentToolId();
    // Only apply scale when in select mode (scale mode uses select tool internally)
    if (currentToolId !== "select") return;

    // Don't apply isScale if we just created a shape
    // This prevents applying isScale to newly created shapes
    if (justCreatedShapeRef.current) {
      return;
    }

    const unsupportedTypes = ["customImage", "line", "arrow", "customLabel"];
    selectedShapeIds.forEach((id) => {
      const shape = editor.getShape(id);
      if (!shape) return;
      if (unsupportedTypes.includes(shape.type)) return;

      // Only update if the shape doesn't already have isScale: true
      if (!(shape.props as any)?.isScale) {
        editor.updateShape({
          ...shape,
          props: {
            ...shape.props,
            isScale: true,
          } as any,
        });
      }
    });
  }, [selectedShapeIds, isScaleMode, editor]);

  // Handle pointer down/up for hand tool to show grabbing cursor
  React.useEffect(() => {
    const canvas = document.querySelector(".tl-canvas") as HTMLElement;
    if (!canvas) return;

    const handlePointerDown = () => {
      if (editor.getCurrentToolId() === "hand") {
        canvas.style.setProperty("cursor", "grabbing");
      }
    };

    const handlePointerUp = () => {
      if (editor.getCurrentToolId() === "hand") {
        canvas.style.setProperty("cursor", "grab");
      }
    };

    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointerup", handlePointerUp);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointerup", handlePointerUp);
    };
  }, [editor]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts (especially Shift+letter) when typing in search, return address, etc.
      if (isTypingInInput(e)) return;

      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "H") {
        editor.run(
          () => {
            const currentShape = editor.getShape(selectedShape.id);
            if (currentShape) {
              editor.updateShape({
                ...currentShape,
                meta: { hidden: !currentShape.meta?.hidden },
              });
            }
          },
          { ignoreShapeLock: true }
        );
        e.stopPropagation();
      }

      // Toggle isLocked with Ctrl+Shift+L
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "L") {
        editor.run(() => {
          const currentShape = editor.getShape(selectedShape.id);
          if (currentShape) {
            editor.updateShape({
              ...currentShape,
              isLocked: !currentShape.isLocked,
            });
          }
        });
        e.stopPropagation();
      }

      if (e.altKey && (e.ctrlKey || e.metaKey) && e.key === "c") {
        setCopyProps(selectedShape.props);

        e.stopPropagation();
      }

      if (e.altKey && (e.ctrlKey || e.metaKey) && e.key === "v") {
        if (copyProps) {
          editor.run(() => {
            const { w, h, ...otherProps } = copyProps;

            // Filter out props that are not in the selected shape
            const filteredProps = Object.keys(otherProps)
              .filter((key) => key in selectedShape.props)
              .reduce((acc: { [key: string]: any }, key) => {
                acc[key] = otherProps[key];
                return acc;
              }, {} as { [key: string]: any });

            editor.updateShape({
              ...selectedShape,
              props: {
                ...selectedShape.props,
                ...filteredProps,
              },
            });
          });
        }
        e.stopPropagation();
      }

      // ── Tidy-up + ordering shortcuts ──────────────────────────────
      // Mounted globally here (BottomToolPanel is always present while
      // the editor is rendered) so they fire regardless of which
      // sidebars are open. Each handler requires an active selection;
      // tldraw's own editor methods are no-ops on empty selections so
      // the early `getSelectedShapeIds` check is mostly a perf guard.

      // Tidy up — Cmd/Ctrl + Alt + T. Reuses the same `runTidyUp` util
      // the RotateComponent UI button uses, and dispatches the
      // `flyer:distribute-mode` event so the RotateComponent (when
      // mounted) updates its visible "Tidy" selector chip to match.
      if ((e.ctrlKey || e.metaKey) && e.altKey && e.code === "KeyT") {
        const ids = editor.getSelectedShapeIds();
        if (ids.length >= 2) {
          e.preventDefault();
          e.stopPropagation();
          const shapes = ids.map((id) => editor.getShape(id)).filter(Boolean) as any[];
          runTidyUp(editor, shapes);
          window.dispatchEvent(new CustomEvent("flyer:distribute-mode", { detail: { mode: "tidy" } }));
        }
      }

      // Stacking-order shortcuts. Mac and PC use different combos per
      // the design spec (the Excel feedback) — Mac stays with
      // Cmd-based combos, PC uses Ctrl+Shift for the "to back/front"
      // variants to avoid clashing with the OS-level Ctrl+[ / Ctrl+]
      // history navigation that some Windows shells bind.
      //
      // Routes through the same `safe*` helpers the right-click
      // → Reorder menu uses (`UiOverrides.tsx`). The safe variants
      // skip canvas-infrastructure shapes (cut lines, frame
      // background, address-box backdrop, etc.) so "Send to back" stops
      // ABOVE that infra floor instead of disappearing behind the
      // canvas. tldraw's plain `editor.sendToBack` doesn't know about
      // our infra shapes and would shove the user's content underneath
      // them, which was the bug reported when using only the keyboard
      // shortcut.
      //
      // `bringToFront` has no `safe*` variant — there's no infra at
      // the top of the z-stack, so tldraw's default is fine.
      // `stopImmediatePropagation` prevents tldraw's default `]`/`[`
      // bindings (registered on the same window) from also firing
      // their non-safe ordering ops on top of ours.
      const stackingKey =
        e.key === "[" || e.code === "BracketLeft" ? "[" : e.key === "]" || e.code === "BracketRight" ? "]" : null;
      if (stackingKey) {
        const ids = editor.getSelectedShapeIds();
        if (ids.length > 0) {
          const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
          const mod = isMac ? e.metaKey : e.ctrlKey;
          // "To front/back" needs the extra modifier — Alt on Mac,
          // Shift on PC. "Forward/backward" is just the plain mod.
          const toEdge = isMac ? mod && e.altKey : mod && e.shiftKey;
          const oneStep = mod && !e.altKey && !e.shiftKey;
          if (toEdge) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            editor.markHistoryStoppingPoint(stackingKey === "]" ? "bring to front" : "send to back");
            if (stackingKey === "]") editor.bringToFront(ids);
            else safeSendToBack(editor);
          } else if (oneStep) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            editor.markHistoryStoppingPoint(stackingKey === "]" ? "bring forward" : "send backward");
            if (stackingKey === "]") safeBringForward(editor);
            else safeSendBackward(editor);
          }
        }
      }

      // Handle shortcuts for tools
      const toolShortcuts: { [key: string]: string } = {
        H: "hand",
        V: "select",
        K: "scale",
        "Shift+R": "customRect",
        "Shift+L": "line",
        "Shift+A": "arrow",
        "Shift+O": "customEllipse",
        "Shift+T": "customText",
      };

      const shortcut = e.key ? (e.shiftKey ? `Shift+${e.key.toUpperCase()}` : e.key.toUpperCase()) : "";
      const toolId = toolShortcuts[shortcut];

      if (toolId) {
        handleToolClick(toolId, undefined, toolId);
        setSelectedToolIds((prev) => ({
          ...prev,
          select: toolId === "hand" || toolId === "select" || toolId === "scale" ? toolId : prev.select,
          customRect:
            toolId === "line" || toolId === "arrow" || toolId === "customRect" || toolId === "customEllipse"
              ? toolId
              : prev.customRect,
          [toolId]: toolId,
        }));
        const selectedTool = toolsArray.find((t) => t.id === toolId);
        if (selectedTool) {
          setSelectedIcons((prev) => {
            const currentIcon = prev[toolId];
            const newIcon = selectedTool.icon;
            const shapeTools = ["customRect", "customEllipse", "line", "arrow", "customTriangle"];
            return {
              ...prev,
              select:
                toolId === "hand" ? (
                  <HandToolIcon />
                ) : toolId === "select" ? (
                  <CursorToolIcon />
                ) : toolId === "scale" ? (
                  <ScaleToolIcon />
                ) : (
                  prev.select
                ),
              // customRect always shows ShapeToolIcon, never changes
              // Don't update customRect icon even if a shape tool is selected via shortcut
              [toolId]: shapeTools.includes(toolId)
                ? prev.customRect // Keep ShapeToolIcon for customRect category
                : React.isValidElement(newIcon as unknown as React.ReactElement<any>)
                ? React.cloneElement(newIcon as unknown as React.ReactElement<any>, {
                    isActive: true,
                    isMainPanel: true,
                    color: "white",
                  })
                : currentIcon,
            };
          });
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.shiftKey && !(e.altKey || e.metaKey)) {
        const currentToolId = editor.getCurrentToolId();
        const placementTools = [
          "customRect",
          "customEllipse",
          "line",
          "arrow",
          "customTriangle",
          "customText",
          "customImage",
        ];
        // Don't remove cursor if:
        // 1. Using drawing tools (customDraw, customHighlight, erase)
        // 2. Using placement tools (shapes, text, image) - they use crosshair
        // 3. Scale mode is active
        const keepCursor =
          ["customDraw", "customHighlight", "erase"].includes(currentToolId || "") ||
          placementTools.includes(currentToolId || "") ||
          isScaleModeRef.current;
        if (!keepCursor) {
          const canvas = document.querySelector(".tl-canvas") as HTMLElement;
          if (canvas) {
            canvas.style.removeProperty("cursor");
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [selectedShape, setSelectedShape, handleToolClick, toolsArray]);

  // Bring to front die lines, safety lines, and cut lines - ensuring they're always above all other elements
  useEffect(() => {
    const bringDieLinesToFront = () => {
      try {
        const ids = [
          // Safety lines (dash border lines)
          "shape:frontBorderDashLine",
          "shape:backBorderDashLine",
          // Cut lines (red lines)
          "shape:frontRedLine",
          "shape:backRedLine",
          "shape:frontFoldLine",
          "shape:backFoldLine",
          // Labels
          "shape:frontLabel",
          "shape:backLabel",
          // Die cut lines - using proper shape IDs
          "shape:diecutLineFront",
          "shape:diecutLineBack",
          // Cut lines
          "shape:cutLineFront",
          "shape:cutLineBack",
          // A6 Safety lines
          "shape:a6SafetyLineFront",
          "shape:a6SafetyLineBack",
          // Tearaway lines
          "shape:TearawayCutLineFront",
          "shape:TearawayCutLineBack",
          "shape:tearawayFlyerBuilderCutlineFront",
          "shape:tearawayFlyerBuilderCutlineBack",
          "shape:tearawaySafetyLineFront",
          "shape:tearawaySafetyLineBack",
          // AddressBox - always keep on top so it's never hidden by other elements
          "shape:addressBox",
          // CustomMeasure - always keep on top so it's visible above all shapes including AddressBox
          "shape:customMeasure",
        ];

        editor.run(
          () => {
            try {
              // Use bringToFront instead of bringForward to ensure these shapes are always on top
              editor.bringToFront(ids as TLShapeId[]);
            } catch (error) {
              console.error("Error bringing die/safety lines to front:", error);
            }
          },
          { ignoreShapeLock: true }
        );
      } catch (error) {
        console.error("Error in die/safety lines ordering:", error);
      }
    };

    // Run initially and when selection changes
    bringDieLinesToFront();

    // Coalesce all "bring die lines to front" requests into a single
    // `requestAnimationFrame` callback. Without this, dragging N selected
    // shapes fires the change handler N times per pointer move (~60fps),
    // each scheduling its own `setTimeout(0)` — at 128 selected that's
    // ~128 invocations of `editor.bringToFront(20 IDs)` per move, all
    // doing redundant work since the die lines only need to end at the
    // top once per frame.
    let pendingRaf: number | null = null;
    const scheduleBringToFront = () => {
      if (pendingRaf !== null) return;
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        bringDieLinesToFront();
      });
    };

    const unregisterAfterCreate = editor.sideEffects.registerAfterCreateHandler("shape", scheduleBringToFront);
    const unregisterAfterChange = editor.sideEffects.registerAfterChangeHandler("shape", scheduleBringToFront);

    return () => {
      unregisterAfterCreate();
      unregisterAfterChange();
      if (pendingRaf !== null) cancelAnimationFrame(pendingRaf);
    };
  }, [editor, selectedShape]);

  // DISABLED: Auto-centering camera on orientation change to prevent unwanted centering on save
  // useEffect(() => {
  //   editor.zoomToFit({
  //     animation: { duration: 0 },
  //   });
  //   const currentZoom = editor.getZoomLevel() / 1.1;
  //   editor.setCamera({ x: 0, y: 0, z: currentZoom });

  //   // Get the center of the page
  //   const pageBounds = editor.getCurrentPageBounds();
  //   if (pageBounds) {
  //     const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
  //     const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
  //     editor.centerOnPoint({ x: centerX, y: centerY });
  //   }
  // }, [currentOrientations, editor]);

  // Update shapes position after orientation change
  const prevOrientationRef = useRef<string | undefined>(undefined);
  const prevFormatRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const isInitialLoad = prevOrientationRef.current === undefined && prevFormatRef.current === undefined;
    // Also consider transition from empty string to a value as initial load
    const isOrientationInitializing = prevOrientationRef.current === "" && currentOrientations !== "";

    // Detect first format selection on a new design (format goes from empty/undefined to a value)
    const isFirstFormatSelection = !prevFormatRef.current && format;

    if ((isInitialLoad || isOrientationInitializing) && !isFirstFormatSelection) {
      // First load or orientation initializing: Don't reset positions, just record values
      prevOrientationRef.current = currentOrientations;
      prevFormatRef.current = format;
      setIsUpdateShapesPosition(false);
    } else {
      // Subsequent loads: Check if values actually changed
      const orientationChanged = prevOrientationRef.current !== currentOrientations;
      const formatChanged = prevFormatRef.current !== format;

      if (orientationChanged || formatChanged) {
        // Check if this change came from undo/redo sync — if so, skip re-layout
        // because tldraw already reverted the shapes via undo.
        const isUndoSync = (useStore.getState() as StoreType).isUndoOrientationSync;
        if (isUndoSync) {
          // Just update refs so we don't detect another "change" next render.
          // Don't reset the flag here — useFlyerLayout needs to read it too.
          prevOrientationRef.current = currentOrientations;
          prevFormatRef.current = format;
          return;
        }

        // Real change detected: Reset positions
        setIsUpdateShapesPosition(true);
        prevOrientationRef.current = currentOrientations;
        prevFormatRef.current = format;

        // Reset flag after shapes are updated and camera has centered
        // Increased to 700ms to allow time for camera centering (500ms delay + 200ms margin)
        setTimeout(() => {
          setIsUpdateShapesPosition(false);
        }, 700);
      }
    }
  }, [format, currentOrientations]);

  // Snap mode
  useEffect(() => {
    editor.user.updateUserPreferences({ isSnapMode: true });
  }, []);

  // Center camera on initialization only (not on every save)
  useEffect(() => {
    const currentCamera = editor.getCamera();
    const isInitialLoad = currentCamera.x === 0 && currentCamera.y === 0 && currentCamera.z === 1;

    if (isInitialLoad) {
      setTimeout(() => {
        editor.zoomToFit({
          animation: { duration: 0 },
        });
        const currentZoom = editor.getZoomLevel() / 1.1;
        editor.setCamera({ x: 0, y: 0, z: currentZoom });

        // Get the center of the page
        const pageBounds = editor.getCurrentPageBounds();
        if (pageBounds) {
          const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
          const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
          editor.centerOnPoint({ x: centerX, y: centerY });
        }
      }, 100);
    }
  }, [editor]); // Only run once when editor is available

  useEffect(() => {
    let keyPressed = false;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore shortcuts when typing in search, return address, etc.
      if (isTypingInInput(event)) return;

      if ((event.key === "k" || event.key === "K") && !keyPressed) {
        keyPressed = true;
        // Toggle scale mode
        setIsScaleMode((prevMode) => {
          const newMode = !prevMode;
          // Toggle the CSS-var override so resize-handle cursors flip
          // immediately to the custom scale glyph (and back to tldraw
          // defaults when scale mode turns off).
          setScaleModeCursorActive(newMode);
          const selectedShapeIds = editor.getSelectedShapeIds();
          // List of shape types that don't support custom properties like isScale
          const unsupportedTypes = ["customImage", "line", "arrow", "customLabel", "frame"];

          selectedShapeIds.forEach((id) => {
            const shape = editor.getShape(id);
            // Skip shapes that don't support custom properties
            if (shape && unsupportedTypes.includes(shape.type)) return;
            if (shape) {
              editor.updateShape({
                ...shape,
                props: {
                  ...shape.props,
                  isScale: newMode,
                },
              });
            }
          });

          // Use setTimeout to ensure the cursor is set after any other cursor changes
          setTimeout(() => {
            const canvas = document.querySelector(".tl-canvas") as HTMLElement;
            if (canvas) {
              // The base canvas cursor stays cleared; the custom
              // scale arrows kick in only when the user hovers a
              // resize handle (via the CSS-var override).
              canvas.style.removeProperty("cursor");
            }
          }, 0);

          return newMode;
        });
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "k" || event.key === "K") {
        keyPressed = false;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [editor]);

  // Update zoom level when it changes
  useEffect(() => {
    const currentZoom = editor.getZoomLevel();
    if (Math.abs(currentZoom - zoomLevel) > 0.001) {
      setZoomLevel(currentZoom);
    }
  }, [editor, zoom, zoomLevel]);

  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({
    select: false,
    customRect: false,
    customText: false,
    message: false,
    updateShapesPosition: false,
  });
  const tooltipTimeoutRef = useRef<{ [key: string]: NodeJS.Timeout | null }>({});

  const handleTooltipOpen = (id: string) => {
    const timeout = tooltipTimeoutRef.current[id];
    if (timeout) {
      clearTimeout(timeout);
    }
    setTooltipOpen((prev) => ({ ...prev, [id]: true }));
    tooltipTimeoutRef.current[id] = setTimeout(() => {
      setTooltipOpen((prev) => ({ ...prev, [id]: false }));
    }, 250);
  };

  const handleTooltipClose = (id: string) => {
    const timeout = tooltipTimeoutRef.current[id];
    if (timeout) {
      clearTimeout(timeout);
      tooltipTimeoutRef.current[id] = null;
    }
    setTooltipOpen((prev) => ({ ...prev, [id]: false }));
  };

  const { document: tlDocument } = getSnapshot(editor.store);

  if (isEmptyState) return null;

  return (
    <div
      className="flex items-center gap-3 absolute z-[100000000000] bottom-8 left-1/2 -translate-x-1/2 bg-white rounded-[8px] px-[8px] py-[8px]"
      style={{
        boxShadow: `
        0 33px 9px 0 rgba(105, 105, 105, 0.00),
        0 21px 8px 0 rgba(105, 105, 105, 0.02),
        0 12px 7px 0 rgba(105, 105, 105, 0.08),
        0 5px 5px 0 rgba(105, 105, 105, 0.14),
        0 1px 3px 0 rgba(105, 105, 105, 0.17)
      `,
      }}
    >
      {icons.map(({ id, tools, tip, component: Icon }) => (
        <div
          key={id}
          data-tour={`tool-${id}`}
          className="flex items-center gap-1 cursor-pointer"
          onMouseEnter={() => {
            // Don't set hover if any popover is open (prevent highlighting other icons when popover is open)
            if (!anchorEl) {
              setHoveredIconId(id);
            }
          }}
          onMouseLeave={() => {
            // Only clear hover if no popover is open
            if (!anchorEl) {
              setHoveredIconId(null);
            }
          }}
        >
          <Tooltip
            title={tip}
            arrow
            classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
            open={tooltipOpen[id]}
          >
            <div
              onMouseEnter={() => handleTooltipOpen(id)}
              onMouseLeave={() => handleTooltipClose(id)}
              onClick={(e) => {
                // Clear hover state on click to prevent incorrect highlighting
                setHoveredIconId(null);
                if (tools.length > 0) {
                  // Check if there's a previously selected tool for this category
                  const selectedToolId = selectedToolIds[id];
                  // Check current editor tool
                  const currentToolId = editor.getCurrentToolId();

                  // Helper function to check if icon is ShapeToolIcon
                  const isShapeToolIcon = (icon: React.ReactElement | null): boolean => {
                    if (!icon || !React.isValidElement(icon)) return false;
                    const iconType = icon.type as any;
                    // Check if it's ShapeToolIcon by comparing the function/component
                    return (
                      iconType === ShapeToolIcon ||
                      iconType?.displayName === "ShapeToolIcon" ||
                      iconType?.name === "ShapeToolIcon" ||
                      (typeof iconType === "function" && iconType.toString().includes("ShapeToolIcon"))
                    );
                  };

                  // Check if the current icon is ShapeToolIcon
                  const currentIconIsShapeTool = isShapeToolIcon(selectedIcons[id]);

                  // Check if selectedToolId is in the tools list (even if it equals id)
                  const selectedToolInList = selectedToolId
                    ? tools.some((tool: any) => tool.id === selectedToolId)
                    : false;
                  // Check if current tool is in the available tools list
                  const currentToolExists = currentToolId
                    ? tools.some((tool: any) => tool.id === currentToolId)
                    : false;

                  // Determine which tool to use:
                  // 1. If icon is ShapeToolIcon, always open menu
                  // 2. Else if selectedToolId is in tools list, use it (specific icon is shown)
                  // 3. Else if current tool is in tools list, use currentToolId
                  // 4. Otherwise, open menu
                  if (currentIconIsShapeTool || id === "customImage") {
                    // ShapeToolIcon or Image tool → always open menu
                    handleOpenPopover(e, tools, id);
                  } else {
                    let toolToUse: string | null = null;
                    if (selectedToolInList && selectedToolId) {
                      toolToUse = selectedToolId;
                    } else if (currentToolExists && currentToolId) {
                      toolToUse = currentToolId;
                    }

                    if (toolToUse) {
                      // Use the tool directly
                      handleToolClick(toolToUse, undefined, id);
                    } else {
                      // No tool found → open menu
                      handleOpenPopover(e, tools, id);
                    }
                  }
                } else {
                  if (id === "customIcons") {
                    // Exit comments placing mode + close comments sidebar so
                    // the icons panel can take over the right side.
                    const cs = useCommentsStore.getState();
                    if (cs.isPlacing) cs.exitPlacingMode();
                    if (cs.isHistoryOpen) cs.closeHistory();
                    editor.setSelectedShapes([]);
                    setShowIconsPanel(true);
                    setShowImagePanel(false);
                    setShowQRcodePanel(false);
                    setBrandKitAssetsPanel(null);
                    window.dispatchEvent(new CustomEvent("tour:creating-start"));
                  } else if (id === "customQRCode") {
                    // Delegate to handleToolClick which auto-creates QR and shows edit panel
                    handleToolClick("customQRCode");
                  } else {
                    handleToolClick(selectedToolIds[id] || id, undefined, id);
                  }
                }
              }}
              className={cn(
                selectedToolIds[id] === editor.getCurrentToolId() ||
                  (id === "select" && selectedToolIds[id] === "scale" && isScaleMode) ||
                  (id === "customRect" && isShapeToolActive()) ||
                  hoveredIconId === id ||
                  (id === "customImage" && (showImagePanel || brandKitAssetsPanel === "image")) ||
                  (id === "customIcons" && showIconsPanel) ||
                  (id === "customQRCode" && showQRcodePanel)
                  ? "bg-[#EE4360] text-white"
                  : "",
                "flex items-center justify-center p-3 rounded-[8px] w-[48px] h-[48px] hover:bg-[#EE4360] hover:text-white hover:shasow-custom group"
              )}
            >
              {selectedIcons[id] ? (
                React.cloneElement(selectedIcons[id] as React.ReactElement, {
                  isActive:
                    selectedToolIds[id] === editor.getCurrentToolId() ||
                    (id === "select" && selectedToolIds[id] === "scale" && isScaleMode) ||
                    (id === "customRect" && isShapeToolActive()) ||
                    hoveredIconId === id ||
                    (id === "customImage" && (showImagePanel || brandKitAssetsPanel === "image")) ||
                    (id === "customIcons" && showIconsPanel) ||
                    (id === "customQRCode" && showQRcodePanel),
                  isMainPanel: true,
                  color:
                    selectedToolIds[id] === editor.getCurrentToolId() ||
                    (id === "select" && selectedToolIds[id] === "scale" && isScaleMode) ||
                    (id === "customRect" && isShapeToolActive()) ||
                    hoveredIconId === id ||
                    (id === "customImage" && (showImagePanel || brandKitAssetsPanel === "image")) ||
                    (id === "customIcons" && showIconsPanel) ||
                    (id === "customQRCode" && showQRcodePanel)
                      ? "white"
                      : "#0C172C",
                  className: "w-7 h-7",
                })
              ) : (
                <Icon
                  isActive={
                    selectedToolIds[id] === editor.getCurrentToolId() ||
                    (id === "select" && selectedToolIds[id] === "scale" && isScaleMode) ||
                    (id === "customRect" && isShapeToolActive()) ||
                    hoveredIconId === id ||
                    (id === "customImage" && (showImagePanel || brandKitAssetsPanel === "image")) ||
                    (id === "customIcons" && showIconsPanel) ||
                    (id === "customQRCode" && showQRcodePanel)
                  }
                  isMainPanel={true}
                  color={
                    selectedToolIds[id] === editor.getCurrentToolId() ||
                    (id === "select" && selectedToolIds[id] === "scale" && isScaleMode) ||
                    (id === "customRect" && isShapeToolActive()) ||
                    hoveredIconId === id ||
                    (id === "customImage" && (showImagePanel || brandKitAssetsPanel === "image")) ||
                    (id === "customIcons" && showIconsPanel) ||
                    (id === "customQRCode" && showQRcodePanel)
                      ? "white"
                      : "#0C172C"
                  }
                />
              )}
            </div>
          </Tooltip>
          {tools.length > 0 && (
            <>
              <KeyboardArrowUpIcon
                onClick={(e) => {
                  // Clear hover state when clicking arrow to prevent incorrect highlighting
                  setHoveredIconId(null);
                  handleOpenPopover(e, tools, id);
                }}
                className={cn({ "rotate-180": anchorEl && selectedTool === id })}
              />
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                PaperProps={{
                  // @ts-ignore
                  "data-tour": "tool-popover",
                  style: {
                    transform: "translate(-55px,-20px)",
                    minWidth: "210px",
                    padding: "8px",
                  },
                }}
                classes={{ paper: classes.paper }}
              >
                <div className="bg-white rounded-[8px] !top-4" style={{ width: "100%" }}>
                  {currentTools.map((tool: any, index: number) => {
                    return (
                      <Tooltip
                        placement="right"
                        key={index}
                        title={tool.tip}
                        arrow
                        classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
                        open={tooltipOpen[`${tool.id}-${index}`]}
                      >
                        <div
                          onMouseEnter={() => handleTooltipOpen(`${tool.id}-${index}`)}
                          onMouseLeave={() => handleTooltipClose(`${tool.id}-${index}`)}
                          onClick={() => {
                            // Clear hover state BEFORE handling the click to prevent incorrect highlighting
                            setHoveredIconId(null);
                            handleToolClick(tool.id, tool.onClick, selectedTool || undefined);

                            // Update selectedToolIds to show the selected tool in bottom toolbar
                            // This allows clicking the icon directly to use the selected tool
                            if (selectedTool) {
                              setSelectedToolIds((prev) => ({
                                ...prev,
                                [selectedTool]: tool.id,
                              }));
                            }

                            // Close the popover after selecting a tool
                            handleClosePopover();

                            setSelectedIcons((prev) => {
                              // Keep Image icon unchanged — always show ImageToolIcon
                              if (selectedTool === "customImage") return prev;

                              const newIcon = tool.icon;
                              const shapeTools = ["customRect", "customEllipse", "line", "arrow", "customTriangle"];
                              // Update icon for shape tools when selected from popover
                              if (selectedTool === "customRect" && shapeTools.includes(tool.id)) {
                                return {
                                  ...prev,
                                  [selectedTool]: React.isValidElement(newIcon)
                                    ? React.cloneElement(newIcon as unknown as React.ReactElement<any>, {
                                        isActive: true,
                                        isMainPanel: true,
                                        color: "white",
                                        className: "w-7 h-7",
                                      })
                                    : prev[selectedTool],
                                };
                              }
                              return {
                                ...prev,
                                [selectedTool || ""]: React.isValidElement(newIcon)
                                  ? React.cloneElement(newIcon as unknown as React.ReactElement<any>, {
                                      isActive: true,
                                      isMainPanel: true,
                                      color: "white",
                                      className: "w-7 h-7",
                                    })
                                  : prev[selectedTool || ""],
                              };
                            });
                          }}
                          key={index}
                          className={cn(
                            "flex gap-3 p-3 items-center cursor-pointer text-black hover:bg-[#E2E2FC] transition-all",
                            tool.id === selectedToolIds[selectedTool || ""] && "bg-[#E2E2FC]"
                          )}
                        >
                          {tool?.icon && (
                            <div className="w-[24px] h-[24px] flex items-center justify-center [&>svg]:w-[24px] [&>svg]:h-[24px]">
                              {tool.icon}
                            </div>
                          )}
                          <p className="flex-1">{tool.name}</p>
                          {tool.shortcut && (
                            <div className="flex items-center gap-1 ml-auto">
                              <KeyboardIcon />
                              <span className="text-gray-400">{tool.shortcut}</span>
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </Popover>
            </>
          )}
        </div>
      ))}

      <CommentsToolbarButton />

      {/* Separator */}
      <div className="h-[48px] w-[1px] bg-[#E2E2FC] mx-2"></div>

      {/* Undo/Redo Buttons */}
      <Tooltip title="Undo (Ctrl+Z)" arrow classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}>
        <div
          onClick={handleUndo}
          className={cn(
            "flex items-center justify-center p-3 rounded-[8px] w-[48px] h-[48px] hover:bg-[#EE4360] hover:text-white cursor-pointer transition-all",
            hoveredIconId === "undo" && "bg-[#EE4360] text-white"
          )}
          onMouseEnter={() => {
            if (!anchorEl) {
              setHoveredIconId("undo");
            }
          }}
          onMouseLeave={() => {
            if (!anchorEl) {
              setHoveredIconId(null);
            }
          }}
        >
          <UndoIcon isActive={hoveredIconId === "undo"} />
        </div>
      </Tooltip>

      <Tooltip
        title="Redo (Ctrl+Shift+Z)"
        arrow
        classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
      >
        <div
          onClick={handleRedo}
          className={cn(
            "flex items-center justify-center p-3 rounded-[8px] w-[48px] h-[48px] hover:bg-[#EE4360] hover:text-white cursor-pointer transition-all",
            hoveredIconId === "redo" && "bg-[#EE4360] text-white"
          )}
          onMouseEnter={() => {
            if (!anchorEl) {
              setHoveredIconId("redo");
            }
          }}
          onMouseLeave={() => {
            if (!anchorEl) {
              setHoveredIconId(null);
            }
          }}
        >
          <RedoIcon isActive={hoveredIconId === "redo"} />
        </div>
      </Tooltip>

      {/* Separator */}
      <div className="h-[48px] w-[1px] bg-[#E2E2FC] mx-2"></div>

      {/* Zoom Controls */}
      <div className="flex items-center">
        <Tooltip title="Zoom Out" arrow classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}>
          <div
            onClick={handleZoomOut}
            className={cn(
              "flex items-center justify-center p-3 rounded-[8px] w-[48px] h-[48px] hover:bg-[#EE4360] hover:text-white cursor-pointer transition-all",
              hoveredIconId === "zoomOut" && "bg-[#EE4360] text-white"
            )}
            onMouseEnter={() => {
              if (!anchorEl) {
                setHoveredIconId("zoomOut");
              }
            }}
            onMouseLeave={() => {
              if (!anchorEl) {
                setHoveredIconId(null);
              }
            }}
          >
            <RemoveIcon
              style={{
                color: hoveredIconId === "zoomOut" ? "white" : "#0C172C",
                fontSize: "24px",
              }}
            />
          </div>
        </Tooltip>

        <Tooltip
          title={`Zoom: ${Math.floor(zoomLevel * 100)}% - Click to reset to 100%`}
          arrow
          classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
        >
          <div
            onClick={handleZoomReset}
            className={cn(
              "flex items-center justify-center px-3 py-3 rounded-[8px] min-w-[60px] h-[48px] hover:bg-[#EE4360] hover:text-white cursor-pointer transition-all text-sm font-medium",
              hoveredIconId === "zoomLevel" && "bg-[#EE4360] text-white"
            )}
            onMouseEnter={() => {
              if (!anchorEl) {
                setHoveredIconId("zoomLevel");
              }
            }}
            onMouseLeave={() => {
              if (!anchorEl) {
                setHoveredIconId(null);
              }
            }}
          >
            <span
              style={{
                color: hoveredIconId === "zoomLevel" ? "white" : "#0C172C",
                fontSize: "20px",
              }}
            >
              {Math.floor(zoomLevel * 100)}%
            </span>
          </div>
        </Tooltip>

        <Tooltip title="Zoom In" arrow classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}>
          <div
            onClick={handleZoomIn}
            className={cn(
              "flex items-center justify-center p-3 rounded-[8px] w-[48px] h-[48px] hover:bg-[#EE4360] hover:text-white cursor-pointer transition-all",
              hoveredIconId === "zoomIn" && "bg-[#EE4360] text-white"
            )}
            onMouseEnter={() => {
              if (!anchorEl) {
                setHoveredIconId("zoomIn");
              }
            }}
            onMouseLeave={() => {
              if (!anchorEl) {
                setHoveredIconId(null);
              }
            }}
          >
            <AddIcon
              style={{
                color: hoveredIconId === "zoomIn" ? "white" : "#0C172C",
                fontSize: "24px",
              }}
            />
          </div>
        </Tooltip>
      </div>

      {/* <Tooltip
        title="Back to content"
        arrow
        classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
        open={tooltipOpen["backToContent"]}
      >
        <div
          onMouseEnter={() => handleTooltipOpen("backToContent")}
          onMouseLeave={() => handleTooltipClose("backToContent")}
          onClick={() => {
            handleBackToContent();
          }}
          className="group flex items-center justify-center rounded-[4px] w-[50px] h-[50px] hover:bg-[#3e8aee] hover:shasow-custom cursor-pointer  group"
        >
          <svg
            className="group-hover:fill-white fill-[#273754]"
            width="40px"
            height="40px"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.5 4C3.5 3.72386 3.72386 3.5 4 3.5H7C7.27614 3.5 7.5 3.72386 7.5 4C7.5 4.27614 7.27614 4.5 7 4.5H4.5V7C4.5 7.27614 4.27614 7.5 4 7.5C3.72386 7.5 3.5 7.27614 3.5 7V4Z"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20 3.5C20.2761 3.5 20.5 3.72386 20.5 4V7C20.5 7.27614 20.2761 7.5 20 7.5C19.7239 7.5 19.5 7.27614 19.5 7V4.5L17 4.5C16.7239 4.5 16.5 4.27614 16.5 4C16.5 3.72386 16.7239 3.5 17 3.5L20 3.5Z"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 20.5C3.72386 20.5 3.5 20.2761 3.5 20L3.5 17C3.5 16.7239 3.72386 16.5 4 16.5C4.27614 16.5 4.5 16.7239 4.5 17L4.5 19.5H7C7.27614 19.5 7.5 19.7239 7.5 20C7.5 20.2761 7.27614 20.5 7 20.5H4Z"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M20.5 20C20.5 20.2761 20.2761 20.5 20 20.5H17C16.7239 20.5 16.5 20.2761 16.5 20C16.5 19.7239 16.7239 19.5 17 19.5H19.5V17C19.5 16.7239 19.7239 16.5 20 16.5C20.2761 16.5 20.5 16.7239 20.5 17V20Z"
              ></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 9C9.28707 9 7.2802 10.7931 6.6 11.7C6.46667 11.8778 6.46667 12.1222 6.6 12.3C7.2802 13.2069 9.28707 15 12 15C14.7129 15 16.7198 13.2069 17.4 12.3C17.5333 12.1222 17.5333 11.8778 17.4 11.7C16.7198 10.7931 14.7129 9 12 9ZM10.1256 13.6543C9.0093 13.2396 8.14276 12.5335 7.65177 12C8.14276 11.4665 9.0093 10.7604 10.1256 10.3457C9.73626 10.7865 9.5 11.3657 9.5 12C9.5 12.6343 9.73626 13.2135 10.1256 13.6543ZM13.8744 13.6543C14.9907 13.2396 15.8572 12.5335 16.3482 12C15.8572 11.4665 14.9907 10.7604 13.8744 10.3457C14.2637 10.7865 14.5 11.3657 14.5 12C14.5 12.6343 14.2637 13.2135 13.8744 13.6543ZM10.5 12C10.5 11.1716 11.1716 10.5 12 10.5C12.8284 10.5 13.5 11.1716 13.5 12C13.5 12.8284 12.8284 13.5 12 13.5C11.1716 13.5 10.5 12.8284 10.5 12Z"
              ></path>
            </g>
          </svg>
          {/* <EnterFullScreenIcon className="w-8 h-8 text-black group-hover:!text-white" /> */}
      {/* </div>
      </Tooltip> 
      */}

      {/* <div id="pdf-export-button" className="h-9 w-[1px] bg-slate-500"></div>
      <button
        className="bg-rose-400 p-2 cursor-pointer rounded-md"
        onClick={() => {
          const jsonString = JSON.stringify(tlDocument, null, 2); // Convert tlDocument to JSON string
          const blob = new Blob([jsonString], { type: "application/json" });
          const url = URL.createObjectURL(blob); // Create URL for download

          const link = document.createElement("a");
          link.href = url;
          link.download = "tlDocument.json"; // File name for download
          link.click();

          URL.revokeObjectURL(url);
        }}
      >
        <DownloadIcon className="w-8 h-8 text-black" />
      </button> */}
    </div>
  );
};
export default React.memo(BottomToolPanel);

const CommentsToolbarButton: React.FC = React.memo(() => {
  const isPlacing = useCommentsStore((s) => s.isPlacing);
  const togglePlacingMode = useCommentsStore((s) => s.togglePlacingMode);
  const unreadCount = useCommentsStore((s) => s.totalUnreadCount);
  const editor = useEditor();
  const classesTooltip = useTooltipStyles();

  const handleClick = () => {
    // Guest gate: guests don't get the Comments feature. Instead of
    // toggling placing mode, we fire `guest-comments-blocked` so
    // Tldraw.tsx can open the FeatureModal teaser → signup. Avoids
    // prop-drilling `isGuest` + a callback through BottomToolPanel
    // just for this single button.
    if (!authorization.getUser()?.id) {
      window.dispatchEvent(new CustomEvent("guest-comments-blocked"));
      return;
    }
    if (!isPlacing) {
      editor.setCurrentTool("select");
    }
    togglePlacingMode();
  };

  const hasUnread = unreadCount > 0;
  const tooltipTitle = isPlacing
    ? "Click anywhere to drop a comment (Esc to cancel)"
    : hasUnread
    ? `Comments — ${unreadCount} unread (C)`
    : "Add comment (C)";

  return (
    <Tooltip title={tooltipTitle} arrow classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}>
      <div
        onClick={handleClick}
        className={cn(
          "group relative flex items-center justify-center p-3 rounded-[8px] w-[48px] h-[48px] cursor-pointer transition-all",
          isPlacing ? "bg-[#EE4360] text-white" : "hover:bg-[#EE4360] hover:text-white"
        )}
      >
        {/* Same shape as the placing cursor (public/cursors/comment.svg) so the
            tool's bottom-island affordance and the active cursor stay visually
            in sync. Stroke currentColor lets the hover/active states tint it. */}
        <svg width="24" height="24" viewBox="10 -2 65 65" fill="none" stroke="currentColor" strokeWidth="4">
          <path
            d="M15.0034 21.4772C14.8473 13.5164 20.2377 6.51376 27.9735 4.62775L39.2007 1.89052C63.1316 -3.94392 83.5072 19.9531 73.9631 42.6609L71.2066 49.2191C68.5078 55.6402 62.1652 59.7654 55.2014 59.6289L16.6977 58.8739C16.1608 58.8634 15.728 58.4306 15.7175 57.8937L15.0034 21.4772Z"
            strokeLinejoin="round"
          />
        </svg>
        {hasUnread && !isPlacing && (
          <span
            aria-label={`${unreadCount} unread`}
            className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#EE4360] ring-2 ring-white transition-colors group-hover:bg-white group-hover:ring-[#EE4360]"
          />
        )}
      </div>
    </Tooltip>
  );
});
CommentsToolbarButton.displayName = "CommentsToolbarButton";

const useStyles = makeStyles({
  paper: {
    borderRadius: "10px !important",
    padding: "0 !important",
    display: "flex",
    justifyContent: "center",
    boxShadow: "none",
    minHeight: "0",
  },
});
